import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { NIP_LENGTH } from "@shared";

interface Props {
  value: string;
  onChange: (next: string) => void;
}

/**
 * Saisie du NIP en quatre cases separees.
 *
 * Un champ unique de quatre chiffres marche moins bien pour un jeune de six
 * ans : les cases montrent combien il en reste a taper, et chaque case est une
 * grosse cible tactile. `inputMode="numeric"` fait apparaitre le pave
 * numerique sur telephone plutot que le clavier complet.
 */
export default function NipInput({ value, onChange }: Props) {
  const { t } = useTranslation();
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: NIP_LENGTH }, (_, i) => value[i] ?? "");

  function setDigit(index: number, digit: string): void {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join("").slice(0, NIP_LENGTH));
  }

  function focusAt(index: number): void {
    inputs.current[index]?.focus();
    inputs.current[index]?.select();
  }

  return (
    <div className="nip" role="group" aria-label={t("login.nipLabel")}>
      {digits.map((digit, index) => (
        <input
          // Position fixe dans une grille de taille constante : l'index est ici
          // une clé stable, pas un pis-aller.
          key={index}
          id={`nip-${index}`}
          ref={(el) => {
            inputs.current[index] = el;
          }}
          className="nip__box"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={t("login.nipDigit", { position: index + 1, total: NIP_LENGTH })}
          value={digit}
          onChange={(e) => {
            // On ne garde que le dernier chiffre saisi : cela gere aussi bien
            // la frappe normale que le remplacement d'une case deja remplie.
            const typed = e.target.value.replace(/\D/g, "").slice(-1);
            if (typed === "") return;
            setDigit(index, typed);
            if (index < NIP_LENGTH - 1) focusAt(index + 1);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              e.preventDefault();
              if (digit !== "") {
                setDigit(index, "");
              } else if (index > 0) {
                setDigit(index - 1, "");
                focusAt(index - 1);
              }
            } else if (e.key === "ArrowLeft" && index > 0) {
              focusAt(index - 1);
            } else if (e.key === "ArrowRight" && index < NIP_LENGTH - 1) {
              focusAt(index + 1);
            }
          }}
          onPaste={(e) => {
            // Colle un NIP complet d'un coup plutot que case par case.
            e.preventDefault();
            const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, NIP_LENGTH);
            if (pasted !== "") {
              onChange(pasted);
              focusAt(Math.min(pasted.length, NIP_LENGTH - 1));
            }
          }}
          onFocus={(e) => {
            e.target.select();
          }}
        />
      ))}
    </div>
  );
}
